import { useEffect, useState } from 'react';
import type { Onboarding } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { useAddMsGroupMembers, useCreateMsGroup } from './api.js';
import { applyGroupMailSettings } from './graphGroupSettings.js';

const VANITY_DOMAIN = 'nscatalyst.com';

/** Default Catalyst owner/members preloaded into every new onboarding MS group. */
const DEFAULT_OWNERS = 'damon@nscatalyst.com';
const DEFAULT_MEMBERS = [
  'damon@nscatalyst.com',
  'JQuinio@nscatalyst.com',
  'kristina@nscatalyst.com',
  'ron@nscatalyst.com',
  'renae@nscatalyst.com',
].join('\n');

interface MsGroupModalProps {
  isOpen: boolean;
  onboarding: Onboarding;
  onClose: () => void;
}

/** Strips the TLD from an email's domain, e.g. "joe@veloit.com" → "veloit". */
function domainBase(email: string | null): string {
  if (!email) return '';
  const at = email.lastIndexOf('@');
  if (at < 0) return '';
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  if (!domain) return '';
  return domain.replace(/\.[^.]+$/, '');
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function MsGroupModal({ isOpen, onboarding, onClose }: MsGroupModalProps): JSX.Element {
  const createMutation = useCreateMsGroup();
  const addMembersMutation = useAddMsGroupMembers();
  const resetMutation = createMutation.reset;

  const [displayName, setDisplayName] = useState('');
  const [mailNickname, setMailNickname] = useState('');
  const [description, setDescription] = useState('');
  const [owners, setOwners] = useState('');
  const [members, setMembers] = useState('');
  const [phase, setPhase] = useState<'create' | 'settings' | 'members' | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [finishedWithWarnings, setFinishedWithWarnings] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const base = domainBase(onboarding.opportunityContactEmail);
    setDisplayName(onboarding.msGroupName ?? base);
    setMailNickname(base ? slugify(base) : '');
    setDescription('');
    setOwners(DEFAULT_OWNERS);
    setMembers(DEFAULT_MEMBERS);
    setPhase(null);
    setWarnings([]);
    setFinishedWithWarnings(false);
    resetMutation();
  }, [
    isOpen,
    onboarding.id,
    onboarding.opportunityContactEmail,
    onboarding.msGroupName,
    resetMutation,
  ]);

  const alias = slugify(mailNickname);
  const previewEmail = alias ? `${alias}@${VANITY_DOMAIN}` : '—';

  // Three phases: create group (+owners) → PATCH mail settings with the signed-in
  // member's DELEGATED Graph token (app-only tokens can't update them) → add members,
  // so autoSubscribeNewMembers covers every member on add.
  const handleCreate = async (): Promise<void> => {
    const memberEmails = members
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setWarnings([]);
    setFinishedWithWarnings(false);
    setPhase('create');
    let created: Onboarding;
    try {
      created = await createMutation.mutateAsync({
        id: onboarding.id,
        input: {
          displayName: displayName.trim(),
          mailNickname: alias,
          description: description.trim() || null,
          ownerEmails: owners
            .split(/[\s,;]+/)
            .map((s) => s.trim())
            .filter(Boolean),
          memberEmails: [],
        },
      });
    } catch {
      setPhase(null);
      return; // createMutation.error renders below.
    }

    const collected: string[] = [];
    if (created.msGroupId) {
      setPhase('settings');
      const settings = await applyGroupMailSettings(created.msGroupId);
      if (!settings.ok) {
        collected.push(
          `The group was created, but "Allow external senders" and "Send copies to members" ` +
            `were NOT applied: ${settings.message ?? 'unknown error'} Set them in the Microsoft ` +
            `365 admin center (or via your PowerShell script).`,
        );
      }
    }

    if (memberEmails.length > 0) {
      setPhase('members');
      try {
        const result = await addMembersMutation.mutateAsync({
          id: onboarding.id,
          input: { memberEmails },
        });
        if (result.unresolved.length > 0) {
          collected.push(`Could not add these members: ${result.unresolved.join(', ')}`);
        }
      } catch (err) {
        collected.push(
          err instanceof ApiError
            ? `Adding members failed: ${err.message}`
            : 'Adding members failed — add them manually in the Microsoft 365 admin center.',
        );
      }
    }

    setPhase(null);
    if (collected.length === 0) {
      onClose();
    } else {
      setWarnings(collected);
      setFinishedWithWarnings(true);
    }
  };

  const error =
    createMutation.error instanceof ApiError
      ? createMutation.error.message
      : createMutation.error
        ? 'Failed to create the Microsoft 365 group.'
        : null;

  const busy = phase !== null;
  const busyLabel =
    phase === 'create'
      ? 'Creating group…'
      : phase === 'settings'
        ? 'Applying mail settings…'
        : phase === 'members'
          ? 'Adding members…'
          : 'Create group';
  const canCreate = Boolean(displayName.trim() && alias);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Microsoft 365 group"
      size="lg"
      footer={
        finishedWithWarnings ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} isLoading={busy} disabled={!canCreate}>
              {busy ? busyLabel : 'Create group'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Review the group name and email before creating. Suggested from the opportunity contact{' '}
          {onboarding.opportunityContactEmail ? (
            <span className="font-medium text-slate-700">
              {onboarding.opportunityContactName ?? onboarding.opportunityContactEmail}
            </span>
          ) : (
            <span className="italic">— no contact on the opportunity, enter values manually</span>
          )}
          .
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Group name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="veloit"
          />
          <TextField
            label="Email alias"
            value={mailNickname}
            onChange={(e) => setMailNickname(e.target.value)}
            placeholder="veloit"
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-500">Group email will be: </span>
          <span className="font-medium text-slate-800">{previewEmail}</span>
          <p className="mt-1 text-xs text-slate-500">
            External senders will be allowed and members will receive copies of group
            conversations and events (applied with your Microsoft sign-in after the group is
            created).
          </p>
        </div>

        <TextAreaField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            label="Owners (one email per line, optional)"
            value={owners}
            onChange={(e) => setOwners(e.target.value)}
            rows={3}
            placeholder="owner@nscatalyst.com"
          />
          <TextAreaField
            label="Members (one email per line, optional)"
            value={members}
            onChange={(e) => setMembers(e.target.value)}
            rows={3}
            placeholder="user@nscatalyst.com"
          />
        </div>

        {warnings.length > 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <p className="font-medium">Group created with warnings:</p>
            <ul className="mt-1 list-disc pl-5">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
